import { useId } from "react";
import $ from "jquery";
import ReactSummernote from "react-summernote";
import "react-summernote/dist/react-summernote.css";
import "bootstrap/js/dist/dropdown";
import "bootstrap/js/dist/tooltip";
import "bootstrap/js/dist/modal";
import { cx } from "../../utils/cx";
import styles from "./RichEditor.module.scss";

type SummernoteButtonContext = {
    invoke: (namespace: string) => void;
    options: {
        icons: Record<string, string>;
        langInfo: {
            paragraph: {
                left: string;
                center: string;
                right: string;
                justify: string;
            };
        };
    };
};

/**
 * Summernote's built-in justify buttons crash when placed on the toolbar.
 * These call the same commands and return a finished button.
 */
function alignButton(
    iconKey: "alignLeft" | "alignCenter" | "alignRight" | "alignJustify",
    command: string,
    labelKey: "left" | "center" | "right" | "justify",
) {
    return (context: SummernoteButtonContext) =>
        $.summernote.ui
            .button({
                contents: $.summernote.ui.icon(context.options.icons[iconKey]),
                tooltip: context.options.langInfo.paragraph[labelKey],
                click: () => {
                    context.invoke(command);
                },
            })
            .render();
}

const alignButtons = {
    alignLeft: alignButton("alignLeft", "editor.justifyLeft", "left"),
    alignCenter: alignButton("alignCenter", "editor.justifyCenter", "center"),
    alignRight: alignButton("alignRight", "editor.justifyRight", "right"),
    alignJustify: alignButton("alignJustify", "editor.justifyFull", "justify"),
};

/** One Summernote group: name, then the button keys. */
export type RichEditorToolbarGroup = [string, string[]];

/** Bold, italic, alignment, link, and HTML view — the Checkout Warnings toolbar. */
export const richEditorToolbar: RichEditorToolbarGroup[] = [
    ["font", ["bold", "italic"]],
    ["para", ["alignLeft", "alignCenter", "alignRight", "alignJustify"]],
    ["insert", ["link"]],
    ["view", ["codeview"]],
];

export interface RichEditorProps {
    /** Floating label, same role as Textarea. */
    label: string;
    /** HTML string. Empty string keeps the field blank. */
    value?: string;
    /** Called with HTML. A cleared editor emits `""`. */
    onChange?: (value: string) => void;
    placeholder?: string;
    helperText?: string;
    error?: boolean | string;
    required?: boolean;
    disabled?: boolean;
    fullWidth?: boolean;
    /**
     * Summernote toolbar groups. Defaults to bold, italic, alignment, link, and HTML view.
     * Read once on mount.
     */
    toolbar?: RichEditorToolbarGroup[];
    className?: string;
}

/** Summernote treats a blank editor as a paragraph with a break. */
export function normalizeRichHtml(html: string): string {
    const text = html
        .replace(/<br\s*\/?>/gi, "")
        .replace(/&nbsp;/gi, " ")
        .replace(/<[^>]*>/g, "")
        .trim();

    return text.length === 0 ? "" : html;
}

/**
 * Treez DS rich text field.
 * Summernote (same engine as the page editor) inside the Textarea shell:
 * filled surface, floating label, and focus border.
 */
export function RichEditor({
    label,
    value = "",
    onChange,
    placeholder = "",
    helperText,
    error,
    required = false,
    disabled = false,
    fullWidth = false,
    toolbar = richEditorToolbar,
    className,
}: RichEditorProps) {
    const reactId = useId();
    const labelId = `ds-rich-editor-${reactId}`;
    const messageId = `${labelId}-msg`;
    const hasError = Boolean(error);
    const message = typeof error === "string" ? error : helperText;

    return (
        <div
            className={cx(
                styles.root,
                fullWidth && styles.fullWidth,
                className,
            )}
            data-disabled={disabled || undefined}
            data-error={hasError || undefined}
            data-ui="rich-editor"
        >
            <div className={styles.field}>
                <span className={styles.label} id={labelId}>
                    {label}
                    {required ? " *" : ""}
                </span>
                <div className={styles.editor}>
                    <ReactSummernote
                        disabled={disabled}
                        onChange={contents => {
                            const next = normalizeRichHtml(contents ?? "");
                            if (next === normalizeRichHtml(value)) {
                                return;
                            }
                            onChange?.(next);
                        }}
                        options={{
                            toolbar,
                            buttons: alignButtons,
                            placeholder,
                            dialogsFade: false,
                            disableDragAndDrop: true,
                            followingToolbar: false,
                            tooltip: true,
                            codemirror: {
                                mode: "text/html",
                                htmlMode: true,
                                lineNumbers: true,
                                lineWrapping: true,
                            },
                            popover: {
                                image: [],
                                table: [],
                                air: [],
                                link: [["link", ["linkDialogShow", "unlink"]]],
                            },
                        }}
                        value={value}
                    />
                </div>
            </div>
            {message ? (
                <div className={styles.helper} id={messageId}>
                    {message}
                </div>
            ) : null}
        </div>
    );
}

RichEditor.displayName = "RichEditor";
